import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Chip, CircularProgress,
  Stack, Button, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Calculate as CalculateIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import { payrollApi } from '../../../api/payrollApi';
import {
  PayCycle,
  PayCycleLabels,
  PayrollRunDetailDto,
  PayrollRunStatus,
  PayrollRunStatusLabels,
  PayrollRunStatusColors,
  SalaryComponentType,
  PayrollComponentDetailDto,
  PayrollAdjustmentType,
  PayrollAdjustmentTypeLabels,
  PayrollEmployeeDetailDto,
} from '../../../types/payrollTypes';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-IN');
};

const getRunPeriodLabel = (detail: PayrollRunDetailDto) => {
  if ((detail.payCycle ?? PayCycle.Monthly) === PayCycle.Weekly) {
    return `${formatDate(detail.periodStart)} - ${formatDate(detail.periodEnd)}`;
  }

  return `${MONTHS[detail.month - 1]} ${detail.year}`;
};

const deductionComponentTypes = [
  SalaryComponentType.Deduction,
  SalaryComponentType.TaxComponent,
  SalaryComponentType.LeaveComponent,
];

const formatDays = (value?: number | null) => (value ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatMoney = (value?: number | null) => `Rs. ${(value ?? 0).toLocaleString('en-IN')}`;

const PayrollRunDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<PayrollRunDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [adjustmentEmployee, setAdjustmentEmployee] = useState<PayrollEmployeeDetailDto | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<PayrollAdjustmentType>(PayrollAdjustmentType.Bonus);
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentRemarks, setAdjustmentRemarks] = useState('');
  const [savingAdjustment, setSavingAdjustment] = useState(false);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await payrollApi.getPayrollRunDetail(id);
        setDetail(data);
      } catch {
        toast.error('Failed to load payroll run details');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [id]);

  const reloadDetail = async () => {
    if (!id) return;
    const data = await payrollApi.getPayrollRunDetail(id);
    setDetail(data);
  };

  const getApiErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.message || error?.response?.data || fallback;

  const handleWorkflow = async (action: () => Promise<void>, successMessage: string, fallbackError: string) => {
    try {
      await action();
      toast.success(successMessage);
      await reloadDetail();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, fallbackError));
    }
  };

  const handleSaveAdjustment = async () => {
    if (!id || !adjustmentEmployee) return;
    const amount = Number(adjustmentAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error('Enter a valid adjustment amount');
      return;
    }
    setSavingAdjustment(true);
    try {
      await payrollApi.addPayrollAdjustment(id, adjustmentEmployee.employeeId, {
        adjustmentType,
        componentName: PayrollAdjustmentTypeLabels[adjustmentType],
        amount,
        remarks: adjustmentRemarks || null,
      });
      toast.success('Adjustment saved and recalculated');
      setAdjustmentEmployee(null);
      setAdjustmentAmount('');
      setAdjustmentRemarks('');
      await reloadDetail();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to save adjustment'));
    } finally {
      setSavingAdjustment(false);
    }
  };

  const handleRecalculateEmployee = async (employeeId: string) => {
    if (!id) return;
    try {
      await payrollApi.recalculatePayrollEmployee(id, employeeId);
      toast.success('Employee recalculated');
      await reloadDetail();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to recalculate employee'));
    }
  };

  const handleExport = async () => {
    if (!id || !detail) return;
    try {
      const blob = await payrollApi.exportPayrollRun(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payroll-run-${detail.year}-${String(detail.month).padStart(2, '0')}.xls`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export payroll run');
    }
  };

  const renderBreakdownTooltip = (title: string, components: PayrollComponentDetailDto[]) => (
    <Box sx={{ minWidth: 240, p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      {components.length === 0 ? (
        <Typography variant="body2">No components</Typography>
      ) : (
        components.map((component) => (
          <Stack
            key={`${title}-${component.componentId}`}
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ py: 0.2 }}
          >
            <Typography variant="body2">{component.componentName}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatMoney(component.amount)}
            </Typography>
          </Stack>
        ))
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) return null;
  const isEditable = detail.status === PayrollRunStatus.Draft || detail.status === PayrollRunStatus.Review;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Tooltip title="Back to Payroll Runs">
          <IconButton onClick={() => navigate('/payroll/runs')}>
            <BackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h5" fontWeight={700}>
          Payroll Run - {getRunPeriodLabel(detail)}
        </Typography>
        <Chip
          label={PayCycleLabels[detail.payCycle ?? PayCycle.Monthly]}
          color="info"
          size="small"
        />
        <Chip
          label={PayrollRunStatusLabels[detail.status]}
          color={PayrollRunStatusColors[detail.status]}
          size="small"
        />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Export">
          <IconButton onClick={handleExport}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        {isEditable && (
          <Button
            variant="outlined"
            startIcon={<CalculateIcon />}
            onClick={() => id && handleWorkflow(() => payrollApi.recalculatePayrollRun(id), 'Payroll recalculated', 'Failed to recalculate payroll')}
          >
            Recalculate All
          </Button>
        )}
        {detail.status === PayrollRunStatus.Draft && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => id && handleWorkflow(() => payrollApi.submitPayrollForReview(id), 'Submitted for review', 'Failed to submit payroll')}
          >
            Submit for Review
          </Button>
        )}
        {detail.status === PayrollRunStatus.Review && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => id && handleWorkflow(() => payrollApi.approvePayrollRun(id), 'Payroll approved', 'Failed to approve payroll')}
          >
            Approve Payroll
          </Button>
        )}
        {detail.status === PayrollRunStatus.Approved && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => id && handleWorkflow(() => payrollApi.processPayrollRun(id), 'Payroll processed and locked', 'Failed to process payroll run')}
          >
            Process Payroll
          </Button>
        )}
        {detail.status === PayrollRunStatus.Processed && (
          <Button
            variant="contained"
            color="success"
            onClick={() => id && handleWorkflow(() => payrollApi.markPayrollRunPaid(id), 'Payroll marked as paid', 'Failed to mark payroll paid')}
          >
            Mark as Paid
          </Button>
        )}
      </Stack>

      <Paper>
        <TableContainer
          sx={{
            maxHeight: 'calc(100vh - 220px)',
            overflow: 'auto',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: 'background.paper',
                  boxShadow: 'inset 0 -1px 0',
                  color: 'text.primary',
                  zIndex: 2,
                },
              }}
            >
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Code</TableCell>
                <TableCell align="right">Calendar Days</TableCell>
                <TableCell align="right">Working Days</TableCell>
                <TableCell align="right">Weekly Off</TableCell>
                <TableCell align="right">Holiday</TableCell>
                <TableCell align="right">Present Days</TableCell>
                <TableCell align="right">Half Day</TableCell>
                <TableCell align="right">Paid Leave</TableCell>
                <TableCell align="right">Payable Days</TableCell>
                <TableCell align="right">LWP</TableCell>
                <TableCell align="right">Absent</TableCell>
                <TableCell align="right">LOP</TableCell>
                <TableCell align="right">Gross Salary</TableCell>
                <TableCell align="right">Earnings</TableCell>
                <TableCell align="right">Deductions</TableCell>
                <TableCell align="right">OT Amount</TableCell>
                <TableCell align="right">Adjustments</TableCell>
                <TableCell align="right">Net Salary</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.employeeDetails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">No employee details</TableCell>
                </TableRow>
              ) : (
                detail.employeeDetails.map((emp) => {
                  const deductions = (emp.componentDetails || [])
                    .filter((component) => deductionComponentTypes.includes(component.type));
                  const earnings = (emp.componentDetails || [])
                    .filter((component) => !deductionComponentTypes.includes(component.type));
                  const adjustmentTotal = (emp.adjustments || []).reduce((sum, adjustment) => sum + adjustment.amount, 0);

                  return (
                    <TableRow key={emp.id}>
                      <TableCell>{emp.employeeName}</TableCell>
                      <TableCell>{emp.employeeCode}</TableCell>
                      <TableCell align="right">{formatDays(emp.totalCalendarDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.scheduledWorkingDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.weeklyOffDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.paidHolidayDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.presentDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.halfDayDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.paidLeaveDays ?? emp.leaveDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.payableDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.unpaidLeaveDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.absentDays)}</TableCell>
                      <TableCell align="right">{formatDays(emp.lopDays)}</TableCell>
                      <TableCell align="right">{formatMoney(emp.grossSalary)}</TableCell>
                      <TableCell align="right">
                        <Tooltip arrow placement="left-start" title={renderBreakdownTooltip('Earnings', earnings)}>
                          <span>{formatMoney(emp.totalEarnings)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip arrow placement="left-start" title={renderBreakdownTooltip('Deductions', deductions)}>
                          <span>{formatMoney(emp.totalDeductions)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{formatMoney(emp.overtimeAmount)}</TableCell>
                      <TableCell align="right">{formatMoney(adjustmentTotal)}</TableCell>
                      <TableCell align="right">{formatMoney(emp.netSalary)}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Add Adjustment">
                            <span>
                              <IconButton
                                size="small"
                                disabled={!isEditable}
                                onClick={() => setAdjustmentEmployee(emp)}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Recalculate">
                            <span>
                              <IconButton
                                size="small"
                                disabled={!isEditable}
                                onClick={() => handleRecalculateEmployee(emp.employeeId)}
                              >
                                <CalculateIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Paper sx={{ mt: 3, p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <HistoryIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>Audit Log</Typography>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {(detail.auditLogs || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">No audit entries yet</Typography>
        ) : (
          <Stack spacing={1}>
            {detail.auditLogs.slice(0, 8).map((log) => (
              <Box key={log.id}>
                <Typography variant="body2" fontWeight={600}>
                  {log.action}{log.employeeName ? ` - ${log.employeeName}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(log.createdAt).toLocaleString()} {log.newValue ? `| ${log.newValue}` : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Dialog open={Boolean(adjustmentEmployee)} onClose={() => setAdjustmentEmployee(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Adjustment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Employee"
              value={adjustmentEmployee?.employeeName ?? ''}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              select
              label="Adjustment Type"
              value={adjustmentType}
              onChange={(event) => setAdjustmentType(Number(event.target.value) as PayrollAdjustmentType)}
              fullWidth
            >
              {Object.entries(PayrollAdjustmentTypeLabels).map(([value, label]) => (
                <MenuItem key={value} value={Number(value)}>{label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Amount"
              type="number"
              value={adjustmentAmount}
              onChange={(event) => setAdjustmentAmount(event.target.value)}
              fullWidth
            />
            <TextField
              label="Remarks"
              value={adjustmentRemarks}
              onChange={(event) => setAdjustmentRemarks(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustmentEmployee(null)} disabled={savingAdjustment}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAdjustment} disabled={savingAdjustment}>
            {savingAdjustment ? 'Saving...' : 'Save & Recalculate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollRunDetailPage;
